/**
 * Tiers List Context
 *
 * Provides shared state and configuration to TierCard components
 * to eliminate prop drilling.
 */
import { createContext, useContext, type FC, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Button text customizations
 */
export interface ButtonTexts {
	join: string;
	signIn: string;
	current: string;
}

/**
 * Context value provided to all child components
 */
export interface TiersListContextValue {
	/** Current user's tier ID (if enrolled) */
	currentTierId?: string;

	/** Credits currency name (e.g., 'points') */
	creditsCurrency: string;

	/** Heading level for card labels (1-6) */
	labelLevel: number;

	/** Whether the current user is a guest */
	isGuest: boolean;

	/** Whether to show tier descriptions */
	showDescription: boolean;

	/** Customizable button texts */
	buttonTexts: ButtonTexts;

	/** Inline styles for buttons from custom colors (optional) */
	buttonInlineStyles?: React.CSSProperties;

	/** Handler for joining the program (optional, only for logged-in not enrolled) */
	onJoinProgram?: () => void;

	/** Loading state for join program (optional) */
	isJoiningProgram?: boolean;

	/** Error message for join program (optional) */
	joinProgramError?: string | null;
}

/**
 * Props for the TiersListProvider component
 */
export interface TiersListProviderProps {
	children: ReactNode;
	value: TiersListContextValue;
}

// ============================================================================
// Context
// ============================================================================

const TiersListContext = createContext<TiersListContextValue | undefined>(undefined);

/**
 * Provider component for tiers list context
 *
 * @example
 * ```tsx
 * <TiersListProvider value={{ currentTierId, creditsCurrency, labelLevel, isGuest }}>
 *   <TierCard tier={tier} />
 * </TiersListProvider>
 * ```
 */
export const TiersListProvider: FC<TiersListProviderProps> = ({ children, value }) => {
	return <TiersListContext.Provider value={value}>{children}</TiersListContext.Provider>;
};

/**
 * Hook to access tiers list context
 *
 * @throws {Error} If used outside of TiersListProvider
 *
 * @example
 * ```tsx
 * const { currentTierId, creditsCurrency, labelLevel } = useTiersListContext();
 * ```
 */
export function useTiersListContext(): TiersListContextValue {
	const context = useContext(TiersListContext);

	if (context === undefined) {
		throw new Error('useTiersListContext must be used within a TiersListProvider');
	}

	return context;
}
